import { useState } from 'react';
import { modificationCategories, wishlistFormSchema } from '@greasemap/validation';
import {
  useAddWishlistItem,
  useDeleteWishlistItem,
  useInstallWishlistItem,
  useUpdateWishlistItem,
} from '../../garage/mutations';
import { formatMoney } from '../../garage/format';
import type { GarageSpend, WishlistItem } from '../../garage/types';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Heading,
  Select,
  Stack,
  Text,
  TextArea,
  TextField,
} from '../../ui';

/** Props for {@link WishlistSection}. */
export interface WishlistSectionProps {
  readonly vehicleId: string;
  readonly wishlist: readonly WishlistItem[];
  readonly estimate: GarageSpend;
}

const CATEGORY_OPTIONS = modificationCategories.map((value) => ({ value, label: value }));

const BLANK_FORM = { category: 'Engine', title: '', estimatedCost: '', url: '', notes: '' };

/** Form values as text, before validation. */
type FormValues = typeof BLANK_FORM;

/** Fills the form from an existing wishlist item so an edit starts from what is stored. */
function formFrom(item: WishlistItem): FormValues {
  return {
    category: item.category,
    title: item.title,
    estimatedCost: item.estimatedCost == null ? '' : String(item.estimatedCost),
    url: item.url ?? '',
    notes: item.notes ?? '',
  };
}

/**
 * What the owner plans to do next, and what they expect it to cost.
 *
 * Installing an item promotes it to a modification while keeping the original estimate, so the
 * difference between what was planned and what was paid stays visible. The same panel adds a new
 * item or edits an existing one, sending the row's `version` back on save so a concurrent change is
 * caught rather than silently overwritten.
 *
 * @param props - Component props.
 * @returns The wishlist section.
 */
export function WishlistSection({
  vehicleId,
  wishlist,
  estimate,
}: WishlistSectionProps): React.JSX.Element {
  const add = useAddWishlistItem(vehicleId);
  const update = useUpdateWishlistItem(vehicleId);
  const remove = useDeleteWishlistItem(vehicleId);
  const install = useInstallWishlistItem(vehicleId);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WishlistItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormValues>(BLANK_FORM);

  const isOpen = open || editing !== null;
  const isSaving = add.isPending || update.isPending;

  function toggleAdd(): void {
    setError(null);

    if (isOpen) {
      setOpen(false);
      setEditing(null);
      return;
    }

    setEditing(null);
    setForm(BLANK_FORM);
    setOpen(true);
  }

  function startEdit(item: WishlistItem): void {
    setError(null);
    setOpen(false);
    setForm(formFrom(item));
    setEditing(item);
  }

  function closeForm(): void {
    setOpen(false);
    setEditing(null);
    setForm(BLANK_FORM);
    setError(null);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = wishlistFormSchema.safeParse({
      category: form.category,
      title: form.title,
      estimatedCost: form.estimatedCost === '' ? undefined : Number(form.estimatedCost),
      url: form.url,
      notes: form.notes,
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the details and try again.');
      return;
    }

    const url = parsed.data.url === undefined || parsed.data.url === '' ? null : parsed.data.url;

    try {
      if (editing !== null) {
        await update.mutateAsync({
          wishlistItemId: editing.id,
          body: {
            category: parsed.data.category,
            title: parsed.data.title,
            estimatedCost: parsed.data.estimatedCost ?? null,
            url,
            notes: parsed.data.notes ?? null,
            version: editing.version,
          },
        });
      } else {
        await add.mutateAsync({
          category: parsed.data.category,
          title: parsed.data.title,
          estimatedCost: parsed.data.estimatedCost ?? null,
          url,
          notes: parsed.data.notes ?? null,
          version: null,
        });
      }

      closeForm();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save that item.');
    }
  }

  const outstanding = wishlist.filter((item) => !item.isInstalled);
  const done = wishlist.filter((item) => item.isInstalled);

  return (
    <Stack gap="lg">
      <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
        <Heading level={2} size="lg">
          Wishlist
        </Heading>
        <Button variant="primary" onClick={toggleAdd}>
          {open ? 'Close' : 'Add to wishlist'}
        </Button>
      </Stack>

      <Stack direction="row" gap="sm" wrap align="center">
        <Badge tone="live">{formatMoney(estimate.total)} planned</Badge>
        {estimate.uncostedCount > 0 && (
          <Badge tone="warning">plus {estimate.uncostedCount} with no estimate</Badge>
        )}
      </Stack>

      {isOpen && (
        <Card ariaLabel={editing !== null ? 'Edit wishlist item' : 'New wishlist item'}>
          <form onSubmit={(event) => void submit(event)}>
            <Stack gap="lg">
              <Select
                label="Category"
                options={CATEGORY_OPTIONS}
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              />
              <TextField
                label="What do you want to fit?"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
              <TextField
                label="Estimated cost"
                inputMode="decimal"
                value={form.estimatedCost}
                onChange={(event) => setForm({ ...form, estimatedCost: event.target.value })}
              />
              <TextField
                label="Link"
                value={form.url}
                onChange={(event) => setForm({ ...form, url: event.target.value })}
                hint="Optional. Include https://"
              />
              <TextArea
                label="Notes"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />

              {error !== null && (
                <p className="gm-field-error" role="alert">
                  {error}
                </p>
              )}

              <Stack direction="row" gap="sm" wrap>
                <Button type="submit" variant="primary" disabled={isSaving}>
                  {editing !== null ? 'Save changes' : 'Save to wishlist'}
                </Button>
                {editing !== null && (
                  <Button type="button" onClick={closeForm}>
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </form>
        </Card>
      )}

      {wishlist.length === 0 ? (
        <EmptyState
          title="Nothing planned yet"
          description="Keep track of the parts you are saving up for."
        />
      ) : (
        <Stack gap="none">
          {[...outstanding, ...done].map((item) => (
            <div key={item.id} className="gm-row">
              <Stack gap="xs">
                <Stack direction="row" gap="sm" align="center" wrap>
                  <Text weight="semibold">{item.title}</Text>
                  <Badge tone="neutral">{item.category}</Badge>
                  {item.isInstalled && <Badge tone="success">Installed</Badge>}
                </Stack>
                <Text size="sm" muted>
                  Estimated {formatMoney(item.estimatedCost)}
                </Text>
                {item.url != null && (
                  <a href={item.url} target="_blank" rel="noreferrer noopener">
                    View the part
                  </a>
                )}
              </Stack>
              <div style={{ marginLeft: 'auto' }}>
                <Stack direction="row" gap="xs" wrap>
                  {!item.isInstalled && (
                    <Button
                      variant="primary"
                      onClick={() => install.mutate({ wishlistItemId: item.id })}
                      disabled={install.isPending}
                      aria-label={`Mark ${item.title} as installed`}
                    >
                      Mark installed
                    </Button>
                  )}
                  <Button onClick={() => startEdit(item)} aria-label={`Edit ${item.title}`}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setPendingDelete(item.id)}
                    aria-label={`Remove ${item.title}`}
                  >
                    Remove
                  </Button>
                </Stack>
              </div>
            </div>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove this wishlist item?"
        message="It is removed from your plans. Anything already installed keeps its modification record."
        confirmLabel="Remove"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) {
            remove.mutate(pendingDelete);
          }

          setPendingDelete(null);
        }}
      />
    </Stack>
  );
}
